import { Divider, Tabs, Empty } from "antd";
import VivoIcon from "../../../assets/icons/vivo_logo.svg?react";
import VivoWordIcon from "../../../assets/icons/Vivo.svg?react";
import FolderOffIcon from "../../../assets/icons/folder_off.svg?react";
import InfoTooltip from "../../../components/UI/InfoTooltip";
import GuideButton from "../../../components/UI/GuideButton";
import TableDowloadButton from "../../../components/UI/DownloadTable";
import FetchWrapper from "../../../components/UI/FetchWraper";
import MarkdownRenderer from "@/components/UI/MarkdownRenderer";
import ConnectionCard, {
  CardProps,
} from "@/components/Connections/ConnectionCard";
import FeedCompositionTable from "@/components/PortfolioConnections/FeedCompositionTable";
import { getTenantTrialConnectionsConfig } from "@/views/PortfolioConnections/portfolioConnectionsConfig";
import { useScopeParams } from "@/hooks/useScopeParams";

interface ConnectionData extends CardProps {
  datasource_type: string;
  company_source: string;
  date_uploaded: string;
  id?: string | number;
}
interface NotesProps {
  notes: string;
}

export default function Connections() {
  return (
    <FetchWrapper endpointSuffix="connections/">
      {(data) => <DataConnections data={data} />}
    </FetchWrapper>
  );
}

function Notes() {
  return (
    <FetchWrapper>
      {(data) => <NotesComponent notes={data.notes} />}
    </FetchWrapper>
  );
}

const DataConnections = ({ data }: { data: ConnectionData[] | null }) => {
  const { id: trialId } = useScopeParams();
  const tenantConfig = getTenantTrialConnectionsConfig(
    window.location.hostname,
    trialId,
  );

  // Prefer the trial's seeded data_connection rows; fall back to the static
  // per-tenant feed set scoped to this trial (e.g. Clario) when the schema
  // has no seeded rows — mirroring the portfolio Connections screen.
  const connections: CardProps[] | null = data?.length
    ? data
    : (tenantConfig?.connections ?? data);

  // Strip tooltip-only fields so the CSV export matches the visible columns.
  const downloadData = connections?.map((card) => {
    const exportRow = { ...card };
    delete exportRow.description;
    delete exportRow.facts;
    return exportRow;
  });

  const items = [
    {
      key: "1",
      label: "Connections",
      children: <ConnectionsCards data={connections} />,
    },
    ...(tenantConfig?.composition
      ? [
          {
            key: "composition",
            label: "Feed Composition",
            children: (
              <div className="pt-4">
                <FeedCompositionTable rows={tenantConfig.composition} />
              </div>
            ),
          },
        ]
      : []),
    {
      key: "2",
      label: "Notes",
      children: <Notes />,
    },
  ];

  return (
    <div
      data-testid="data-configuration-page"
      className="w-full px-[18px] pt-[30px]"
    >
      <div className="flex justify-between align-middle mb-[20px]">
        <h1
          data-testid="data-configuration-heading"
          className="font-bold text-[18px]"
        >
          Data: Configuration{" "}
          <InfoTooltip title="View the types of data, their vendor sources, and the last date of the vendor data output that is included in the current Vivo data version." />
        </h1>
        <div className="flex gap-[10px] items-center ">
          <TableDowloadButton data={downloadData} name="data_connections" />
          <GuideButton pageName="/wiki/guides/data-connections" />
        </div>
      </div>
      <Tabs defaultActiveKey="1" items={items} />
    </div>
  );
};

const ConnectionsCards = ({ data }: { data: CardProps[] | null }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>
    );
  }
  return (
    <div
      data-testid="data-connections-panel"
      className="grid grid-cols-4 gap-[15px]"
    >
      {data.map((item: CardProps, index) => (
        <div
          data-testid="data-connection-card"
          key={`${index}-${item.datasource_type}`}
        >
          <ConnectionCard card={item} />
        </div>
      ))}
    </div>
  );
};

const NotesComponent = ({ notes }: NotesProps) => {
  return (
    <div className="bg-white rounded-md p-4 mt-4 shadow-omni">
      <div className="flex items-center gap-2  mb-2">
        <VivoIcon className="w-10 h-10" />
        <VivoWordIcon className="w-10 h-10" />
        <p className="guide-title">Notes</p>
      </div>
      <Divider />
      {notes && notes.length > 0 ? (
        <div className="m-2 text-sm text-gray-600 markdown">
          <MarkdownRenderer content={notes} />
        </div>
      ) : (
        <div className="flex items-center justify-center min-h-[430px] w-full p-4">
          <div className="flex flex-col items-center justify-center text-[#00000073] text-base gap-2 w-full h-full">
            <FolderOffIcon className="w-8 h-8  mx-auto" />
            <h2 className="text-center text-base font-semibold  mt-2">
              No Notes Available
            </h2>
          </div>
        </div>
      )}
    </div>
  );
};
